import { type ItineraryPricingPreferencesDTO } from "../../../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { type ProviderOffer, type SwissCffProviderOffer } from "../../../../../types/dtos/api/sales/provider-offer.dto";
import { isFiniteNonNegativeNumber } from "../helpers/OfferPricingNumber.helper";
import { providerLabelFor } from "../helpers/OfferPricingProviderLabel.helper";
import { isNonEmptyText } from "../helpers/OfferPricingUrl.helper";
import { type OfferPricingEvaluationContext, type ProviderPricingEvaluation } from "../OfferPricing.types";
import { BaseOfferPricingProvider } from "../OfferPricingProvider.contract";

const ONLINE_OFFERS_REL = "online-offers";
const EXCLUDED_CFF_PRODUCT_ID = 4092;

/**
 * Converts category-of-service preference to Swiss CFF bundle quality code.
 *
 * @param categoryOfService Pricing preference category.
 * @returns Numeric quality code, or `null` when no filter should be applied.
 */
const mapCategoryPreferenceToQuality = ( categoryOfService: ItineraryPricingPreferencesDTO["categoryOfService"] ): number | null => {
    if ( categoryOfService === "1" ) {
        return 1;
    }
    if ( categoryOfService === "2" ) {
        return 2;
    }

    return null;
};

/**
 * Returns true only for CFF bundles that can be used for display pricing.
 */
const isEligibleSwissBundle = (
    bundle: SwissCffProviderOffer,
    qualityPreference: number | null,
): boolean => {
    if ( qualityPreference !== null && bundle.qualityOfService !== qualityPreference ) {
        return false;
    }

    if ( bundle.offers.length === 0 ) {
        return false;
    }

    return bundle.offers.every( ( offer ) => {
        const hasAllowedType = offer.offerType === "TRANSPORT" || offer.offerType === "TRANSPORT_WITH_SEAT_RESERVATION";
        const hasExcludedProductId = offer.productId === EXCLUDED_CFF_PRODUCT_ID;

        return hasAllowedType && hasExcludedProductId === false;
    } );
};

const findOnlineOfferBookingLink = ( bundle: SwissCffProviderOffer ): string | null => {
    const link = bundle.links?.find( ( item ) => item.rel === ONLINE_OFFERS_REL );

    return link !== undefined && isNonEmptyText( link.href ) ? link.href : null;
};

/**
 * Selects one Swiss bundle according to supersaver priority and lowest total price.
 *
 * @remarks Among eligible bundles the cheapest one for the user is preferred,
 *          i.e. the bundle whose offer prices sum to the smallest amount.
 */
const selectSwissBundle = (
    bundles: SwissCffProviderOffer[],
    useSuperSaverIfAvailable: boolean | undefined,
): SwissCffProviderOffer | null => {
    if ( bundles.length === 0 ) {
        return null;
    }

    const candidateBundles = useSuperSaverIfAvailable === true
        ? bundles.filter( ( bundle ) => bundle.offers.some( ( offer ) => offer.refundable === false ) )
        : bundles;
    const fallbackBundles = candidateBundles.length > 0 ? candidateBundles : bundles;

    let selectedBundle: SwissCffProviderOffer | null = null;
    let selectedLowestTotalPrice = Infinity;

    for ( const bundle of fallbackBundles ) {
        const totalPrice = bundle.offers.reduce( ( sum, offer ) => {
            return sum + ( isFiniteNonNegativeNumber( offer.price ) ? offer.price : 0 );
        }, 0 );

        if ( totalPrice < selectedLowestTotalPrice ) {
            selectedLowestTotalPrice = totalPrice;
            selectedBundle = bundle;
        }
    }

    return selectedBundle;
};

/**
 * Groups all swiss_cff offers of a segment and computes one normalized evaluation.
 */
export const evaluateSwissCffProviderOffers = (
    context: OfferPricingEvaluationContext,
    providerOffers: ProviderOffer[],
): ProviderPricingEvaluation => {
    const swissOffers = providerOffers.filter( ( offer ): offer is SwissCffProviderOffer => offer.provider === "swiss_cff" );
    const qualityPreference = mapCategoryPreferenceToQuality( context.pricingPreferences.categoryOfService );
    const eligibleBundles = swissOffers.filter( ( offer ) => isEligibleSwissBundle( offer, qualityPreference ) );
    const selectedBundle = selectSwissBundle( eligibleBundles, context.pricingPreferences.useSuperSaverIfAvailable );

    if ( selectedBundle === null ) {
        return {
            "providerKey":  "swiss_cff",
            "providerName": providerLabelFor( "swiss_cff" ),
            "price":        null,
            "bookingUrl":   null,
        };
    }

    const price = selectedBundle.offers.reduce( ( sum, offer ) => {
        return sum + ( isFiniteNonNegativeNumber( offer.price ) ? offer.price : 0 );
    }, 0 );

    return {
        "providerKey":  "swiss_cff",
        "providerName": providerLabelFor( "swiss_cff" ),
        price,
        "bookingUrl":   findOnlineOfferBookingLink( selectedBundle ),
    };
};

/**
 * Plugin used only to normalize one already-selected Swiss evaluation payload.
 */
export class SwissCffOfferPricingProvider extends BaseOfferPricingProvider {

    /**
     * Creates the Swiss CFF pricing provider plugin.
     */
    public constructor() {
        super( "swiss_cff" );
    }

    /**
     * Evaluates one Swiss CFF provider payload through shared Swiss rules.
     *
     * @param context Segment evaluation context.
     * @param providerOffer Raw provider payload.
     * @returns Normalized provider evaluation.
     */
    public evaluate(
        context: OfferPricingEvaluationContext,
        providerOffer: ProviderOffer,
    ): ProviderPricingEvaluation {
        const typedOffer = providerOffer as SwissCffProviderOffer;
        const result = evaluateSwissCffProviderOffers( context, [ typedOffer ] );

        return result;
    }

}

export const swissCffOfferPricingProviderPlugin = new SwissCffOfferPricingProvider();
